import { Component, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CampaignConfigurationService } from './campaign-configuration.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Address, BusinessProfileModule } from '@vendasta/businesses';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddressFormV2Module } from '@vendasta/businesses';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';

@Component({
  selector: 'app-campaign-configuration-modal',

  imports: [
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    TranslateModule,
    BusinessProfileModule,
    AddressFormV2Module,
    GalaxyAlertModule,
    GalaxyLoadingSpinnerModule,
  ],
  templateUrl: './campaign-configuration-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-configuration-modal.component.scss'],
})
export class CampaignConfigurationModalComponent implements OnInit {
  protected addressForm: FormGroup;
  protected address = signal<Address>({} as Address);
  protected addressChanged = false;
  protected loading = signal<boolean>(true);
  protected formIsValid = signal<boolean>(false);

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CampaignConfigurationModalComponent>,
    private configService: CampaignConfigurationService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
  ) {
    this.addressForm = this.fb.group({});

    // Set dialog width and height
    this.dialogRef.updateSize('600px', 'auto');

    this.addressForm.statusChanges
      .pipe(startWith(this.addressForm.status), takeUntilDestroyed())
      .subscribe((status) => {
        this.formIsValid.set(status === 'VALID');
      });
  }

  ngOnInit(): void {
    this.loadAddress();
  }

  loadAddress(): void {
    this.loading.set(true);
    this.configService.getBusinessAddress().subscribe({
      next: (addressData) => {
        this.address.set(addressData);
        this.loading.set(false);
      },
      error: () => {
        this.address.set({} as Address);
        this.loading.set(false);
      },
    });
  }

  saveAddress(): void {
    if (!this.addressForm.valid) {
      this.snackbar.openErrorSnack(this.translate.instant('CAMPAIGNS.REQUIRED_FIELDS'));
      return;
    }

    const formValues = this.addressForm.getRawValue();

    this.configService.updateBusinessAddress(formValues.addressForm).subscribe({
      next: () => {
        this.snackbar.openSuccessSnack(this.translate.instant('CAMPAIGNS.SAVE_ADDRESS_SUCCESS'));
        this.dialogRef.close(true); // Return true to indicate success
      },
      error: (err) => {
        this.snackbar.openErrorSnack(
          this.translate.instant('CAMPAIGNS.SAVE_ADDRESS_FAILURE') + (err.message || 'Unknown error'),
        );
        this.dialogRef.close(false); // Return false to indicate failure
      },
    });
  }
}
